import { useEffect, useState } from 'react'

/**
 * What counts as a field that can be holding keystrokes of its own.
 *
 * Deliberately broad. A field left out of this would read as "nothing is being
 * typed" while somebody is typing in it, and the Save button would refuse the
 * cursor over an edit that is real — the expensive direction. A field wrongly
 * counted in costs only a button that stays pressable when it could have been
 * dimmed, and a press that finds nothing to save.
 */
const EDITABLE_SELECTOR = 'input, textarea, [contenteditable="true"]'

/**
 * Whether something the reader can type into currently has focus (BRAI-704).
 *
 * The minutes editor's fields buffer their keystrokes and only hand the value
 * up on blur, so the draft the page renders from is a render behind the field
 * being typed in. That lag has exactly one shape: it exists only while such a
 * field has focus, because blurring is what ends it. Asking this is therefore
 * the same as asking whether the draft can be believed when it says nothing
 * has changed.
 *
 * `focusout` carries the element about to receive focus as `relatedTarget`,
 * which is read instead of `document.activeElement` — at that point in the
 * event the latter is briefly `body`, and a reader moving between two fields
 * would otherwise register as having left the editor entirely.
 *
 * @returns True while an input, textarea or contenteditable holds focus.
 */
export function useEditableFieldFocused(): boolean {
  const [focused, setFocused] = useState(false)

  useEffect(() => {
    const isEditable = (node: EventTarget | null) =>
      node instanceof HTMLElement && node.matches(EDITABLE_SELECTOR)

    const onFocusIn = (event: FocusEvent) => setFocused(isEditable(event.target))
    const onFocusOut = (event: FocusEvent) => setFocused(isEditable(event.relatedTarget))

    setFocused(isEditable(document.activeElement))
    document.addEventListener('focusin', onFocusIn)
    document.addEventListener('focusout', onFocusOut)
    return () => {
      document.removeEventListener('focusin', onFocusIn)
      document.removeEventListener('focusout', onFocusOut)
    }
  }, [])

  return focused
}
